// Geteilte Sheet-/Overlay-Bausteine — EIN Schließen-Knopf + EIN Kopf-Muster
// für alle Sheets/Overlays (DRY). Visuell über die globale `.close-btn`-Klasse
// (Tokens-only, siehe style.css) — hier nur Struktur + Verhalten.

// #195-Review: confirm/prompt/select-Sheets liegen über migrierten Views, sind
// aber nicht im View-Stack → ihr Escape darf nicht die View darunter schließen.
import { suspendStack, resumeStack } from "./view-stack.js";
import { zeichen } from "./zeichen.js";
import { gesteuert } from "./ruckel-schalter.js";

const SVG_NS = "http://www.w3.org/2000/svg";

// Das EINE „X"-Pfad-Symbol (vorher in ~10 Sheets dupliziert).
const CLOSE_ICON_PATH =
  "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z";

/** Baut das „X"-SVG (currentColor, 22×22) als DOM-Knoten. */
function buildCloseIcon(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "22");
  svg.setAttribute("height", "22");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("fill", "currentColor");
  path.setAttribute("d", CLOSE_ICON_PATH);
  svg.appendChild(path);
  return svg;
}

/**
 * Der einheitliche Schließen-Knopf für ALLE Sheets/Overlays.
 * `<button class="close-btn" aria-label="Schließen">` mit X-Icon + Klick-Handler.
 */
export function buildCloseButton(onClose: () => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "close-btn";
  btn.setAttribute("aria-label", "Schließen");
  btn.appendChild(buildCloseIcon());
  btn.addEventListener("click", () => onClose());
  return btn;
}

// Pre.750: „Zurück"-Pfeil (←) für Vollseiten-Unterseiten — sie sind echte
// Seiten (kein schwebendes Modal), darum gehört der Ausstieg oben LINKS als
// Zurück, nicht als X rechts. EIN Helfer für HD/Buch/Essenz (DRY).
const BACK_ICON_PATH = "M15.5 4.5 8 12l7.5 7.5L14 21l-9-9 9-9z";

function buildBackIcon(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "24");
  svg.setAttribute("height", "24");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("fill", "currentColor");
  path.setAttribute("d", BACK_ICON_PATH);
  svg.appendChild(path);
  return svg;
}

/** Einheitlicher „Zurück"-Knopf (oben links) für Vollseiten-Unterseiten. */
// 2026-07-05 (Martin, Nav-Vereinheitlichung): EIN Weg fuer alle Ansichten —
// Module koennen closeDrawer()/openDrawer() in app.ts nicht importieren,
// deshalb laufen Menue-Schliessen und -Wiederoeffnen ueber diese Events.
export function requestMenuClose(): void {
  document.dispatchEvent(new CustomEvent("vespera:menu-close"));
}
function requestMenuReopen(): void {
  document.dispatchEvent(new CustomEvent("vespera:menu-reopen"));
}

// 2026-07-06 (Martin): einheitlicher Lade-Hinweis fuers dynamische
// Nachladen (Kontakte, Mediathek, Projekte, Aufgaben ...). Statt leerer
// Flaeche ein Spinner + Text. Design in style.css (.loading-hint*).
/**
 * Die EINE Ladeanzeige der App.
 *
 * ⚠️ 2026-08-30 (Bestandsaufnahme, Paket 6): Es gab zwei Bauer mit
 * getrennten CSS-Familien — `buildLoadingHint` (hier, 4 Aufrufer, kleiner
 * Punkt neben dem Text) und `buildLoadIndicator` (in alltag-view.ts,
 * 3 Aufrufer, grosser Punkt ueber dem Text, mittig schwebend). Dazu elf
 * Stellen, die nur „Lade …" als nackten Text zeigten, ganz ohne Zeichen.
 *
 * Uebernommen wurde die Alltag-Fassung, und zwar nicht aus Geschmack:
 * an ihr haengt Martins Entscheidung vom 20.07. — *„im freien Raum
 * schweben statt oben kleben"* (`min-height: 36vh` gibt dem Zeichen die
 * senkrechte Mitte der leeren Flaeche). Die kleinere Fassung hatte diese
 * Ueberlegung nie.
 *
 * ⚠️ Alle sieben Aufrufer fuellen eine LEERE Flaeche (replaceChildren auf
 * Liste oder Rumpf) — nachgesehen, bevor umgestellt wurde. Fuer einen
 * kleinen Platz mitten im Text waere 36vh falsch; taucht so ein Fall auf,
 * braucht er eine Variante, keinen zweiten Bauer.
 */
export function buildLoadingHint(text = "Lade \u2026"): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "load-indicator";
  wrap.setAttribute("role", "status");
  const spinner = document.createElement("span");
  spinner.className = "load-spinner";
  spinner.setAttribute("aria-hidden", "true");
  const label = document.createElement("span");
  label.className = "load-label";
  label.textContent = text;
  wrap.appendChild(spinner);
  wrap.appendChild(label);
  return wrap;
}

/** Views koennen goToView nicht importieren (app.ts-Scope) — gleiches
 *  Event-Muster wie menu-reopen: der Pfeil einer Arbeiten-View fuehrt
 *  zurueck zum Chat (2026-07-21, NORM fuer Views). */
export function requestGoChat(): void {
  document.dispatchEvent(new CustomEvent("vespera:go-chat"));
}

export function buildBackButton(
  onBack: () => void,
  opts?: { reopenMenu?: boolean },
): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "back-btn";
  btn.setAttribute("aria-label", "Zurück");
  btn.setAttribute("title", "Zurück");
  btn.appendChild(buildBackIcon());
  btn.addEventListener("click", () => {
    onBack();
    // Zurueck = Menue wieder ausklappen — ausser auf Detail-Ebenen
    // (Projekt-Detail -> Liste), die reopenMenu:false setzen.
    if (opts?.reopenMenu !== false) requestMenuReopen();
  });
  return btn;
}

/**
 * Der einheitliche Sheet-Kopf: Titel links, Schließen-Knopf rechts.
 * `<header class="sheet-head"><strong class="sheet-head-title">…</strong><button.close-btn/></header>`
 */
export function buildSheetHeader(
  title: string,
  onClose: () => void,
): HTMLElement {
  const head = document.createElement("header");
  head.className = "sheet-head";
  const titleEl = document.createElement("strong");
  titleEl.className = "sheet-head-title";
  titleEl.textContent = title;
  head.appendChild(titleEl);
  head.appendChild(buildCloseButton(onClose));
  return head;
}

/**
 * Vollseiten-Kopf: Zurueck-Pfeil links + Titel — fuer ALLE Lese-Seiten, die
 * keine eigenen Kopf-Aktionen haben (Material, Mein Alltag). Nutzt dieselben
 * Klassen wie HD/Mein Leben/Essenz (hd-head/hd-title) = ein Format.
 */
/**
 * Alle Koepfe mit ihrem Rueckweg — der DOM entscheidet, welcher gilt.
 *
 * ⚠️ 2026-08-30 (Bestandsaufnahme, Paket 2): Das Wischen vom linken Rand gab
 * es auf 4 von 16 Vollseiten. Eine Geste, die nur an einem Viertel der Orte
 * antwortet, lernt niemand — man probiert sie zweimal, sie tut nichts, und
 * danach probiert man sie nie wieder.
 *
 * ⚠️ NICHT so geloest, wie es zuerst naheliegt: die Geste haengt am
 * Seiten-RUMPF, nicht am Kopf. `buildPageHeader` kennt den Rumpf nicht — in
 * verfassung-view wird der Kopf in `card` gehaengt, gewischt wird auf `el`,
 * einem Element darueber. Ein `head.parentElement` waere dort schlicht falsch
 * gewesen. (Nachgesehen, bevor gebaut wurde.)
 *
 * Deshalb: EIN Waechter am Rumpf des Dokuments, und die Frage "welche Seite
 * ist gemeint?" beantwortet der Baum selbst — der zuletzt gebaute Kopf, der
 * noch sichtbar dasteht.
 */
const rueckwege: Array<{ kopf: HTMLElement; zurueck: () => void }> = [];
let wischWaechterSteht = false;

/**
 * Steht dieser Kopf wirklich vor dem Nutzer?
 *
 * ⚠️ `isConnected` allein reicht NICHT. Eine Ansicht kann im Baum bleiben und
 * nur ausgeblendet sein; dann haette ein Wisch im Chat eine Seite geschlossen,
 * auf der niemand steht. Gemessen wird die Flaeche, nicht die Anwesenheit.
 */
function istSichtbar(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function installiereWischWaechter(): void {
  if (wischWaechterSteht || typeof document === "undefined") return;
  wischWaechterSteht = true;
  attachEdgeSwipeBack(document.body, () => {
    // Von hinten nach vorne: die zuletzt geoeffnete Seite gewinnt (LIFO, wie
    // beim Stapel fuer Escape). Koepfe, die nicht mehr dastehen, fallen dabei
    // heraus — der Baum ist die Wahrheit, keine eigene Buchfuehrung.
    for (let i = rueckwege.length - 1; i >= 0; i--) {
      const e = rueckwege[i];
      if (!e) { rueckwege.splice(i, 1); continue; }
      if (!istSichtbar(e.kopf)) {
        if (!e.kopf.isConnected) rueckwege.splice(i, 1);
        continue;
      }
      e.zurueck();
      return;
    }
  });
}

export function buildPageHeader(
  title: string,
  onBack: () => void,
  // Detail-Ebenen (z.B. Projekt-Detail -> Liste) wollen das Menue NICHT
  // wieder aufklappen — Option wird an buildBackButton durchgereicht.
  // 2026-07-21 (Audit NORM v3, Regel 2): optionaler ⋯-Slot fuer
  // Verwaltungs-Aktionen, die keinen eigenen Tab verdienen.
  opts?: {
    reopenMenu?: boolean;
    menuItems?: Array<{ label: string; onSelect: () => void }>;
    /** Untertitel unter dem Seitentitel (NORM: gehoert in den Kopf). */
    subtitle?: string;
    /** GENAU EINE sichtbare Kopf-Aktion (NORM Regel 2) — mehr ins Menue. */
    action?: HTMLElement;
  },
): HTMLElement {
  const head = document.createElement("header");
  head.className = "hd-head page-head";
  head.setAttribute("aria-label", title);
  // 2267: Pille ist Ort, Titel ist Seite. Auf Alltag den großen Titel nicht toeten.
  // 11.10.2026: Es gibt keine Projekt-Pille im Kopf mehr (das Projekt steht als Zeile auf Zuhause) —
  // der Seitentitel bleibt IMMER stehen; `page-head--ort-in-pille` wird hier nicht mehr gesetzt.
  if (title === "Mein Alltag") {
    head.classList.add("page-head--alltag-titel");
  }
  head.appendChild(buildBackButton(onBack, opts));
  const titles = document.createElement("div");
  titles.className = "hd-head-titles";
  const t = document.createElement("strong");
  t.className = "hd-title";
  t.textContent = title;
  titles.appendChild(t);
  if (opts?.subtitle) {
    const sub = document.createElement("p");
    sub.className = "hd-subtitle";
    sub.textContent = opts.subtitle;
    titles.appendChild(sub);
  }
  head.appendChild(titles);
  // Das schwebende Haus (22.07.–28.10.2026) ist durch den Umschalter „Zuhause | Chat" in der Kopfzeile ersetzt (index.html).
  /*
   * 2026-08-30: Jede Seite mit geteiltem Kopf bekommt das Zurueck-Wischen —
   * hier, weil der Kopf die EINZIGE Stelle ist, die `onBack` fuer alle 21
   * Aufrufer kennt. Die Seiten mit eigenem Kopf (Buch, Essenz, Human Design
   * in app.ts) rufen `attachEdgeSwipeBack` weiterhin selbst; sie kommen hier
   * gar nicht vorbei.
   */
  // Koepfe, die nicht mehr im Baum stehen, fallen hier heraus — sonst waechst
  // die Liste mit jedem Oeffnen (Pruefer-Befund 15.09.: der Datei-Betrachter
  // baut je Oeffnen zwei Koepfe, der Wisch-Handler raeumte nur am Telefon).
  for (let i = rueckwege.length - 1; i >= 0; i--) {
    if (!rueckwege[i]?.kopf.isConnected) rueckwege.splice(i, 1);
  }
  rueckwege.push({ kopf: head, zurueck: onBack });
  installiereWischWaechter();
  if (opts?.action) {
    opts.action.classList.add("page-head-action");
    head.appendChild(opts.action);
  }
  // 8.0.0-pre.2224: Aktion oder ⋯ bekommen auf dem Telefon eine eigene Zeile.
  if (opts?.action || (opts?.menuItems && opts.menuItems.length > 0)) {
    head.classList.add("hd-head--with-tools");
  }
  if (opts?.subtitle && opts?.action) {
    head.classList.add("hd-head--action-and-lead");
  }

  const items = opts?.menuItems ?? [];
  if (items.length > 0) {
    const more = document.createElement("button");
    more.type = "button";
    more.className = "page-head-more";
    more.setAttribute("aria-label", "Mehr");
    more.setAttribute("aria-haspopup", "menu");
    // 2026-08-26 (Norm A): Das Schriftzeichen ⋯ (U+22EF) sprang mit der
    // Schriftgroesse, war in jeder Schrift anders breit und liess sich nicht
    // auf die Strichstaerke der uebrigen Zeichen bringen. Dieser Knopf steht
    // im geteilten Seitenkopf — er erscheint dadurch auf sechzehn Ansichten.
    more.innerHTML = zeichen("mehr", 20);
    const menu = document.createElement("div");
    menu.className = "page-head-menu";
    menu.setAttribute("role", "menu");
    menu.hidden = true;
    for (const it of items) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "page-head-menu-item";
      b.setAttribute("role", "menuitem");
      b.textContent = it.label;
      b.addEventListener("click", () => {
        menu.hidden = true;
        it.onSelect();
      });
      menu.appendChild(b);
    }
    const onOutside = (ev: MouseEvent): void => {
      if (!menu.hidden && !head.contains(ev.target as Node)) {
        menu.hidden = true;
      }
    };
    document.addEventListener("click", onOutside);
    more.addEventListener("click", (ev) => {
      ev.stopPropagation();
      menu.hidden = !menu.hidden;
    });
    head.appendChild(more);
    head.appendChild(menu);
  }
  return head;
}

/**
 * Ein Textfeld waechst mit seinem Inhalt — bis zu einem Deckel aus ZEILEN.
 *
 * ⚠️ 2026-08-30 (Bestandsaufnahme, Paket 5). In beiden Beschriften-Dialogen
 * stand dieselbe Rechnung mit festen Zahlen:
 *
 *   const lineHeight = 22;            // "font-size 15px * 1.45"
 *   const maxH = lineHeight * 6 + 16; // = 148
 *
 * Nachgerechnet an den echten Werten von `.icm-textarea`:
 *
 *   Zeilenhoehe  15 x 1.45      = 21.75   (nicht 22)
 *   sechs Zeilen                = 130.5
 *   + Polster 10 oben + 10 unten = 150.5   (der Code nahm 16)
 *   + Rahmen 1 + 1 (border-box)  = 152.5
 *
 * Der Deckel lag also 4,5 Punkte zu tief: die sechste Zeile wurde
 * angeschnitten, statt sichtbar zu werden.
 *
 * ⚠️ Das saubere Muster steht seit Langem in `app.ts` — mit der Begruendung
 * daneben: *„Er wird aus der tatsaechlichen Zeilenhoehe und dem Innenabstand
 * gerechnet, nicht als feste Pixelzahl — das Feld hat je nach Ansicht 14px
 * oder 16px Schrift, eine feste Zahl waere dort falsch."* Die Lehre lag im
 * Haus und hat die beiden Dialoge nicht erreicht. Deshalb steht sie jetzt
 * hier, an einer Stelle, statt dreimal.
 */
export function wachsendesTextfeld(
  feld: HTMLTextAreaElement,
  maxZeilen: number,
  minZeilen = 2,
): () => void {
  const anpassen = (): void => {
    const stil = window.getComputedStyle(feld);
    const zeile = parseFloat(stil.lineHeight) || 22;
    const innen =
      (parseFloat(stil.paddingTop) || 0) +
      (parseFloat(stil.paddingBottom) || 0) +
      // Bei border-box zaehlt der Rahmen zur Hoehe. Steht dort content-box,
      // sind die Werte 0 und die Summe stimmt trotzdem.
      (stil.boxSizing === "border-box"
        ? (parseFloat(stil.borderTopWidth) || 0) +
          (parseFloat(stil.borderBottomWidth) || 0)
        : 0);
    const max = Math.round(zeile * maxZeilen + innen);
    const min = Math.round(zeile * minZeilen + innen);
    feld.style.height = "auto";
    feld.style.height = `${Math.min(max, Math.max(min, feld.scrollHeight))}px`;
  };
  feld.addEventListener("input", anpassen);
  return anpassen;
}

/**
 * Wisch-vom-linken-Rand-zurueck: native Geste, um eine Vollseite zu schliessen.
 * Bewusst rein passiv (kein preventDefault) -> bricht vertikales Scrollen NIE.
 * Loest nur aus, wenn die Geste am linken Rand startet (<=30px), klar horizontal
 * nach rechts laeuft (>=72px, dx>1.5*dy) und ein einzelner Finger zieht.
 */
export function attachEdgeSwipeBack(
  target: HTMLElement,
  onBack: () => void,
): void {
  const EDGE = 30;
  const THRESHOLD = 72;
  let startX = 0;
  let startY = 0;
  let startTime = 0;
  let tracking = false;
  // Ruckel-Test (Ticket 2912): bei „Scroll-Messfühler aus“ gilt die Rand-Wischgeste nicht.
  target.addEventListener(
    "touchstart",
    gesteuert("wachen", (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        tracking = false;
        return;
      }
      // Nicht auf Formular-/Eingabefeldern starten — ein Cursor-/Auswahl-
      // Ziehen am linken Rand wuerde sonst die Seite versehentlich schliessen.
      const startEl = e.target as Element | null;
      if (startEl?.closest?.("input, textarea, select, [contenteditable]")) {
        tracking = false;
        return;
      }
      const t = e.touches[0];
      tracking = t.clientX <= EDGE;
      startX = t.clientX;
      startY = t.clientY;
      startTime = e.timeStamp;
    }),
    { passive: true },
  );
  target.addEventListener(
    "touchmove",
    gesteuert("wachen", (e: TouchEvent) => {
      if (!tracking) return;
      if (e.touches.length !== 1) {
        tracking = false; // zweiter Finger -> Geste abbrechen
        return;
      }
      const t = e.touches[0];
      if (Math.abs(t.clientY - startY) > Math.abs(t.clientX - startX)) {
        tracking = false; // vertikal -> Scroll gewinnt
      }
    }),
    { passive: true },
  );
  target.addEventListener(
    "touchend",
    gesteuert("wachen", (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const quick = e.timeStamp - startTime <= 600; // bewusster Wisch, kein Wandern
      if (quick && dx >= THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5)
        onBack();
    }),
    { passive: true },
  );
}

// ---------------------------------------------------------------------------
// Pre.796 (#54 A4): Geteilte Bestätigungs-/Eingabe-Dialoge als Sheets.
// Ersetzen window.confirm/window.prompt — Browser-Dialoge passen optisch
// nicht zur App. Aufbau aus den bestehenden .sheet-Bausteinen; gleicher
// z-1600-Modifier wie das Aktions-Menü (liegt über Detail-Sheets z-1100).
// Promise-basiert: confirmSheet → boolean, promptSheet → string | null.
// ---------------------------------------------------------------------------

function buildDialogSheet(
  title: string,
  message: string | undefined,
  onCancel: () => void,
): { el: HTMLElement; card: HTMLElement } {
  const el = document.createElement("div");
  el.className = "sheet sheet--modal sheet--menu";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  const backdrop = document.createElement("div");
  backdrop.className = "sheet-backdrop";
  backdrop.addEventListener("click", onCancel);
  el.appendChild(backdrop);
  const card = document.createElement("div");
  card.className = "sheet-card";
  const h = document.createElement("h2");
  h.className = "sheet-title";
  h.textContent = title;
  card.appendChild(h);
  if (message) {
    const p = document.createElement("p");
    p.className = "sheet-dialog-text";
    p.textContent = message;
    card.appendChild(p);
  }
  el.appendChild(card);
  return { el, card };
}

function dialogActions(
  card: HTMLElement,
  okLabel: string,
  okDanger: boolean,
  onCancel: () => void,
  onOk: () => void,
): HTMLButtonElement {
  const row = document.createElement("div");
  row.className = "sheet-dialog-actions";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "btn btn--ghost";
  cancel.textContent = "Abbrechen";
  cancel.addEventListener("click", onCancel);
  const ok = document.createElement("button");
  ok.type = "button";
  ok.className = okDanger ? "btn btn--danger" : "btn btn--primary";
  ok.textContent = okLabel;
  ok.addEventListener("click", onOk);
  row.append(cancel, ok);
  card.appendChild(row);
  return ok;
}

export function confirmSheet(opts: {
  title: string;
  message?: string;
  confirmLabel?: string;
  danger?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") done(false);
    };
    const done = (v: boolean): void => {
      resumeStack();
      el.remove();
      window.removeEventListener("keydown", onKey);
      resolve(v);
    };
    const { el, card } = buildDialogSheet(opts.title, opts.message, () =>
      done(false),
    );
    const ok = dialogActions(
      card,
      opts.confirmLabel ?? "OK",
      opts.danger === true,
      () => done(false),
      () => done(true),
    );
    document.body.appendChild(el);
    suspendStack();
    window.addEventListener("keydown", onKey);
    ok.focus();
  });
}

export function promptSheet(opts: {
  title: string;
  message?: string;
  value?: string;
  confirmLabel?: string;
}): Promise<string | null> {
  return new Promise((resolve) => {
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") done(null);
    };
    const done = (v: string | null): void => {
      resumeStack();
      el.remove();
      window.removeEventListener("keydown", onKey);
      resolve(v);
    };
    const { el, card } = buildDialogSheet(opts.title, opts.message, () =>
      done(null),
    );
    const input = document.createElement("input");
    input.type = "text";
    input.className = "sheet-dialog-input";
    input.value = opts.value ?? "";
    input.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") done(input.value.trim() || null);
    });
    card.appendChild(input);
    dialogActions(
      card,
      opts.confirmLabel ?? "Speichern",
      false,
      () => done(null),
      () => done(input.value.trim() || null),
    );
    document.body.appendChild(el);
    suspendStack();
    window.addEventListener("keydown", onKey);
    input.focus();
    input.select();
  });
}

/**
 * Pre.804 (#72a): Options-Picker als Sheet — gleiche Mechanik wie
 * confirmSheet/promptSheet, nur mit einer Button-Liste. Abbruch via
 * Backdrop/Escape → null.
 */
/**
 * Die Auswahl der App — eine Liste, ein Tipp, fertig.
 *
 * 2026-08-27 (Martin: "wäre, wenn ich da, wo Sonnet Abo steht, auch die
 * anderen Modelle direkt dort über Drop-down auswählen könnte"): Die Liste
 * kann jetzt zwei Dinge mehr, die eine Modellwahl braucht —
 *
 *   `beschreibung`  eine zweite Zeile unter dem Namen ("Schnell + günstig")
 *   `aktiv`         markiert, was gerade gilt, mit einem Haken
 *
 * Beides ist optional; die beiden bisherigen Aufrufer (Stimmenwahl im
 * Datei-Betrachter, Projektwahl in der Mediathek) bleiben unverändert.
 */
export function selectSheet(opts: {
  title: string;
  message?: string;
  options: ReadonlyArray<{ value: string; label: string; beschreibung?: string }>;
  aktiv?: string;
  /**
   * Ein eigener Bereich unter der Auswahl, der das Blatt NICHT schließt
   * (29.09.2026, Ticket 2736: Vorlese-Tempo und Emojis unter der
   * Antwortlänge). Mit ihm bekommt das Blatt den Schließen-Knopf der App:
   * Wer nur dort etwas umstellt, muss es ohne Auswahl wieder zumachen
   * können — die Fläche daneben erreicht ein Vorleser nicht.
   */
  zusatz?: HTMLElement;
  /**
   * Ein eigener Bereich OBEN, vor der Auswahl (21.10.2026: das Modell steht über der Antwortlänge, ein Blatt für
   * beides). Wie `zusatz` schließt er das Blatt nicht und bringt den Schließen-Knopf mit.
   */
  vorab?: HTMLElement;
  /**
   * Die Auswahl als Gruppe: Name und ein Satz Erklärung über den Stufen, die Stufen als Raster (zwei je Zeile) statt
   * als lange Liste — damit die Gruppen darunter (`zusatz`) ohne Scrollen erreichbar bleiben. (20.10.2026, Blatt der
   * Antwortlänge)
   */
  gruppe?: { name: string; satz: string };
}): Promise<string | null> {
  return new Promise((resolve) => {
    // 21.10.2026 (Prüfer): Fokus ins Blatt, Tab bleibt darin, beim Schließen zurück an den Auslöser.
    const ausloeser = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") done(null);
      if (ev.key === "Tab") haltFokusImBlatt(el, ev);
    };
    const done = (v: string | null): void => {
      resumeStack();
      el.remove();
      window.removeEventListener("keydown", onKey);
      if (ausloeser?.isConnected) ausloeser.focus({ preventScroll: true });
      resolve(v);
    };
    const { el, card } = buildDialogSheet(opts.title, opts.message, () =>
      done(null),
    );
    const list = document.createElement("div");
    list.className = "sheet-dialog-options";
    if (opts.vorab) card.appendChild(opts.vorab);
    if (opts.gruppe) {
      list.classList.add("sheet-dialog-options--raster");
      const name = document.createElement("h3");
      name.className = "schnell-einst-name sheet-gruppe-name";
      name.id = `sheet-gruppe-name-${++gruppenZaehler}`;
      name.textContent = opts.gruppe.name;
      const satz = document.createElement("p");
      satz.className = "schnell-einst-hinweis sheet-gruppe-satz";
      satz.textContent = opts.gruppe.satz;
      list.setAttribute("role", "group");
      list.setAttribute("aria-labelledby", name.id);
      card.append(name, satz);
    }
    for (const o of opts.options) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "btn btn--ghost sheet-dialog-option";
      const istAktiv = opts.aktiv !== undefined && o.value === opts.aktiv;
      if (istAktiv) {
        b.classList.add("is-active");
        b.setAttribute("aria-current", "true");
      }
      if (o.beschreibung) {
        // Zwei Zeilen: der Name traegt, die Erklaerung ist leiser.
        const name = document.createElement("span");
        name.className = "sheet-dialog-option-name";
        name.textContent = o.label;
        const sub = document.createElement("span");
        sub.className = "sheet-dialog-option-sub";
        sub.textContent = o.beschreibung;
        b.append(name, sub);
      } else {
        b.textContent = o.label;
      }
      if (istAktiv) {
        const haken = document.createElement("span");
        haken.className = "sheet-dialog-option-haken";
        haken.innerHTML = zeichen("haken", 18);
        b.appendChild(haken);
      }
      b.addEventListener("click", () => done(o.value));
      list.appendChild(b);
    }
    card.appendChild(list);
    if (opts.zusatz) card.appendChild(opts.zusatz);
    if (opts.zusatz || opts.vorab) {
      const zu = buildCloseButton(() => done(null));
      zu.classList.add("close-btn--corner");
      card.appendChild(zu);
    }
    document.body.appendChild(el);
    suspendStack();
    window.addEventListener("keydown", onKey);
    // Der Titel trägt den Fokus: ohne sichtbaren Ring (kein Bedienelement), Vorleser lesen das Blatt ab hier.
    const titel = card.querySelector<HTMLElement>(".sheet-title");
    if (titel) {
      titel.tabIndex = -1;
      titel.focus({ preventScroll: true });
    }
  });
}

/** Tab und Umschalt+Tab bleiben im Blatt: vom letzten Bedienelement zurück zum ersten und umgekehrt. */
function haltFokusImBlatt(blatt: HTMLElement, ev: KeyboardEvent): void {
  const ziele = [...blatt.querySelectorAll<HTMLElement>("button, [href], input, select, textarea, [tabindex]")].filter(
    (z) => !z.hasAttribute("disabled") && z.tabIndex >= 0 && !z.closest("[hidden]"),
  );
  if (ziele.length === 0) return;
  const erstes = ziele[0];
  const letztes = ziele[ziele.length - 1];
  const aktiv = document.activeElement;
  if (!blatt.contains(aktiv) || (ev.shiftKey && (aktiv === erstes || !ziele.includes(aktiv as HTMLElement)))) {
    ev.preventDefault();
    (ev.shiftKey ? letztes : erstes).focus();
  } else if (!ev.shiftKey && aktiv === letztes) {
    ev.preventDefault();
    erstes.focus();
  }
}

/** Für eindeutige Kennungen, falls zwei Blätter zugleich im Dokument stehen (selectSheet, `gruppe`). */
let gruppenZaehler = 0;
